import type { InputBaseProps } from "@mui/material";
import { styled } from "@mui/material";
import type { ComponentPropsWithRef } from "react";

import type { IconButtonProps } from "@citrine/atoms/icon-button";

import { FormIconButton } from "./buttons";
import { Select as UnstyledSelect } from "./select";
import { TextField as UnstyledTextField } from "./text-field";

export const Select = styled(UnstyledSelect)({});

export interface ContainerProps extends ComponentPropsWithRef<"div"> {
  multiline?: boolean;
  size?: InputBaseProps["size"];
  isEditing?: boolean;
}
export const Container = styled("div", {
  shouldForwardProp: (
    propName: keyof ContainerProps,
  ): propName is keyof ComponentPropsWithRef<"div"> =>
    !["multiline", "size", "isEditing"].includes(propName),
})<ContainerProps>(({ theme, multiline, size, isEditing }) => [
  {
    alignItems: "center",
    display: "flex",
    flex: isEditing ? 1 : undefined,
    fontWeight: 500,
    overflow: isEditing ? "visible" : "auto",
  },
  multiline
    ? {}
    : {
        height: theme.spacing(size === "small" ? 4 : 5),
      },
]);

export const ErrorMessage = styled("div")({ alignSelf: "start" });

export interface ValueContainerProps extends ComponentPropsWithRef<"div"> {
  hasError?: boolean;
  minRows?: number;
  multiline?: boolean;
  size?: InputBaseProps["size"];
}
export const ValueContainer = styled("div", {
  shouldForwardProp: (
    propName: keyof ValueContainerProps,
  ): propName is keyof ComponentPropsWithRef<"div"> =>
    !["hasError", "minRows", "multiline", "size"].includes(propName),
})<ValueContainerProps>(({ theme, hasError, minRows = 1, multiline, size }) => [
  {
    display: "flex",
    overflow: "hidden",
  },
  multiline
    ? {
        alignItems: "flex-start",
        minHeight: `calc(${minRows} * ${theme.spacing(
          size === "small" ? 1 : 1.75,
        )} + ${theme.spacing(3)})`,
        padding: theme.spacing(size === "small" ? 0.875 : 1.125, 1.5),
      }
    : {
        alignItems: "center",
        padding: theme.spacing(0, 1.5),
      },
  hasError
    ? {
        [`&, & > ${ErrorMessage}`]: {
          color: theme.palette.error.main,
        },
      }
    : undefined,
]);

export interface ValueProps extends ComponentPropsWithRef<"div"> {
  asPlaceholder?: boolean;
}
export const Value = styled("div", {
  shouldForwardProp: (propName: keyof ValueProps) =>
    propName !== "asPlaceholder",
})<ValueProps>(({ theme, asPlaceholder }) => ({
  color: asPlaceholder ? theme.palette.grey[500] : undefined,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "break-spaces",
}));

export interface ActionsProps extends ComponentPropsWithRef<"div"> {
  active?: boolean;
  multiline?: boolean;
  size?: InputBaseProps["size"];
}
export const Actions = styled("div", {
  shouldForwardProp: (
    propName: keyof ActionsProps,
  ): propName is keyof ComponentPropsWithRef<"div"> =>
    !["active", "multiline", "size"].includes(propName),
})<ActionsProps>(({ theme, active, multiline, size }) => ({
  alignItems: "center",
  alignSelf: multiline ? "flex-start" : "center",
  display: "flex",
  flex: 0,
  margin: active ? 0 : undefined,
  maxHeight: theme.spacing(size === "small" ? 4 : 5),

  ".MuiFormControl-root + &": {
    border: "1px solid",
    borderColor: theme.palette.grey[100],
    borderLeftWidth: 0,
  },
  [`${Select} + &`]: {
    // this is a weird hack to deal with the focused border
    boxSizing: "content-box",
    height: "calc(100% - 2px)",
  },
  [`${Select}:has(.Mui-focused) + &`]: {
    borderWidth: "2px 2px 2px 0",
    height: theme.spacing(size === "small" ? 3.5 : 4.5),
    marginTop: "-0.5px",
    paddingTop: "0.5px",
  },
}));

export const IconButton = styled(FormIconButton)<IconButtonProps>(
  ({ theme, size }) => ({
    padding: theme.spacing(size === "small" ? 0.625 : 1.25),
  }),
);

export const SquareButton = styled(IconButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
}));

export const ConfirmButton = styled(SquareButton)(({ theme }) => ({
  background: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,

  ":hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.common.white,
  },
}));

export const BaseTextField = styled(UnstyledTextField)(({ size, theme }) => ({
  "& .MuiInputBase-root": {
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    height: "100%",
  },

  "& input": {
    height: "unset",
    padding: theme.spacing(0, 1.5),
  },

  "& textarea": {
    padding: theme.spacing(size === "small" ? 1.25 : 1.5, 1.5),
  },
}));

export const TextField = styled(BaseTextField)({
  "& .MuiInputBase-root.MuiInputBase-adornedEnd": {
    paddingRight: 0,
  },
});
